import { useInView } from 'react-intersection-observer';
import { styled } from '@mui/system';
import React from 'react';

const HighlightWrapper = styled('div')(({ highlight }) => ({
  transition: 'all 0.5s',
  ...(highlight === 'true'
    ? {
        backgroundColor: 'rgba(121, 201, 18, 0.1)',
        boxShadow: `0px 0px 10px 10px rgba(121, 201, 18, 0.1)`
      }
    : {})
}));

// Basic runtime guard to avoid crashing when an invalid component is passed
const isRenderableComponent = (Component) => {
  if (!Component) return false;
  const type = typeof Component;

  // Regular function components / class components
  if (type === 'function') return true;

  // React.lazy and other element types are objects with internal $$typeof
  if (type === 'object' && Component.$$typeof) return true;

  return false;
};

const LazyComponent = ({ component: Component, setIsVisible, highlight, id, ...rest }) => {
  // The IO is kept so setIsVisible still drives the chained-visibility
  // pattern in pages/Home (sections downstream can react to upstream
  // visibility), but the render itself no longer gates on inView.
  // Gating produced empty placeholders on first paint, which let the
  // Footer slip into the viewport and turned the React mount into a
  // 0.24 CLS spike on Lighthouse.
  const { ref } = useInView({
    triggerOnce: true,
    rootMargin: '10px',
    onChange: (inView) => {
      if (inView && setIsVisible) {
        setIsVisible(id || true);
      }
    }
  });

  const isHighlighted = React.useMemo(() => `${highlight === (id || '')}`, [highlight, id]);

  const canRender = isRenderableComponent(Component);

  return (
    <HighlightWrapper id={id} ref={ref} highlight={isHighlighted}>
      {canRender ? <Component {...rest} /> : null}
    </HighlightWrapper>
  );
};

export default LazyComponent;
